// Telegram Web App utilities

import { getDeviceType } from "./deviceUtils";

// Check if app is running inside Telegram
export const isTelegramWebApp = () => {
  return !!(window.Telegram && window.Telegram.WebApp);
};

// Get Telegram Web App instance
export const getTelegramWebApp = () => {
  if (isTelegramWebApp()) {
    return window.Telegram.WebApp;
  }
  return null;
};

// Check if a specific method is supported in the current Telegram version
const isMethodSupported = (methodName) => {
  const webApp = getTelegramWebApp();
  return webApp && typeof webApp[methodName] === 'function';
};

// Get Telegram WebApp version
const getTelegramVersion = () => {
  const webApp = getTelegramWebApp();
  return webApp ? webApp.version : null;
};

// Fullscreen and viewport management using official Telegram API
export const viewportManager = {
  // Request fullscreen mode using official Telegram API
  requestFullscreen: () => {
    const webApp = getTelegramWebApp();

    // Check if requestFullscreen is supported
    if (!isMethodSupported('requestFullscreen')) {

      const deviceType = getDeviceType();
      console.warn(`⚠️ requestFullscreen not supported in Telegram WebApp version ${getTelegramVersion()}, falling back to expand`);
      if (!webApp.isExpanded && deviceType === 'mobile') {
        return viewportManager.expand();
      }
    }

    try {
      webApp.requestFullscreen();
      console.log('🔥 Requested fullscreen mode via Telegram API');
      return true;
    } catch (error) {
      console.warn('⚠️ requestFullscreen failed:', error.message, 'falling back to expand');
      return viewportManager.expand();
    }
  },

  // Exit fullscreen mode
  exitFullscreen: () => {
    const webApp = getTelegramWebApp();

    if (!isMethodSupported('exitFullscreen')) {
      console.warn('⚠️ exitFullscreen not supported in this Telegram version');
      return false;
    }

    try {
      webApp.exitFullscreen();
      console.log('📱 Exited fullscreen mode');
      return true;
    } catch (error) {
      console.warn('⚠️ exitFullscreen failed:', error.message);
      return false;
    }
  },

  // Check if app is in fullscreen mode
  isFullscreen: () => {
    const webApp = getTelegramWebApp();
    try {
      return webApp ? (webApp.isFullscreen || false) : false;
    } catch (error) {
      return false;
    }
  },

  // Expand to full height (fallback)
  expand: () => {
    const webApp = getTelegramWebApp();
    const deviceType = getDeviceType();
    if (webApp && typeof webApp.expand === 'function') {
      try {
        if (!webApp.isExpanded && deviceType === 'mobile') {
          webApp.expand();
          console.log('📱 WebApp expanded to full height');
          return true;
        }
      } catch (error) {
        console.warn('⚠️ expand failed:', error.message);
        return false;
      }
    }
    return false;
  },

  // Check if app is expanded
  isExpanded: () => {
    const webApp = getTelegramWebApp();
    try {
      return webApp ? (webApp.isExpanded || false) : false;
    } catch (error) {
      return false;
    }
  },

  // Get viewport height
  getViewportHeight: () => {
    const webApp = getTelegramWebApp();
    try {
      return webApp ? (webApp.viewportHeight || window.innerHeight) : window.innerHeight;
    } catch (error) {
      return window.innerHeight;
    }
  },

  // Get viewport stable height (excluding keyboard)
  getViewportStableHeight: () => {
    const webApp = getTelegramWebApp();
    try {
      return webApp ? (webApp.viewportStableHeight || window.innerHeight) : window.innerHeight;
    } catch (error) {
      return window.innerHeight;
    }
  },

  // Toggle fullscreen mode
  toggleFullscreen: () => {
    if (viewportManager.isFullscreen()) {
      return viewportManager.exitFullscreen();
    } else {
      return viewportManager.requestFullscreen();
    }
  },

  // Listen to viewport changes
  onViewportChanged: (callback) => {
    const webApp = getTelegramWebApp();
    if (webApp && typeof webApp.onEvent === 'function') {
      try {
        webApp.onEvent('viewportChanged', callback);
      } catch (error) {
        console.warn('⚠️ Failed to add viewportChanged listener:', error.message);
      }
    }
  },

  // Remove viewport change listener
  offViewportChanged: (callback) => {
    const webApp = getTelegramWebApp();
    if (webApp && typeof webApp.offEvent === 'function') {
      try {
        webApp.offEvent('viewportChanged', callback);
      } catch (error) {
        console.warn('⚠️ Failed to remove viewportChanged listener:', error.message);
      }
    }
  },

  // Listen to fullscreen changes
  onFullscreenChanged: (callback) => {
    const webApp = getTelegramWebApp();
    if (webApp && typeof webApp.onEvent === 'function') {
      try {
        webApp.onEvent('fullscreenChanged', callback);
      } catch (error) {
        console.warn('⚠️ Failed to add fullscreenChanged listener:', error.message);
      }
    }
  },

  // Remove fullscreen change listener
  offFullscreenChanged: (callback) => {
    const webApp = getTelegramWebApp();
    if (webApp && typeof webApp.offEvent === 'function') {
      try {
        webApp.offEvent('fullscreenChanged', callback);
      } catch (error) {
        console.warn('⚠️ Failed to remove fullscreenChanged listener:', error.message);
      }
    }
  }
};

// Haptic feedback functions
export const hapticFeedback = {
  // Light impact - for simple button taps
  light: () => {
    const webApp = getTelegramWebApp();
    if (webApp && webApp.HapticFeedback) {
      webApp.HapticFeedback.impactOccurred('light');
    }
  },

  // Medium impact - for important actions
  medium: () => {
    const webApp = getTelegramWebApp();
    if (webApp && webApp.HapticFeedback) {
      webApp.HapticFeedback.impactOccurred('medium');
    }
  },

  // Heavy impact - for critical actions
  heavy: () => {
    const webApp = getTelegramWebApp();
    if (webApp && webApp.HapticFeedback) {
      webApp.HapticFeedback.impactOccurred('heavy');
    }
  },

  // Success notification
  success: () => {
    const webApp = getTelegramWebApp();
    if (webApp && webApp.HapticFeedback) {
      webApp.HapticFeedback.notificationOccurred('success');
    }
  },

  // Error notification
  error: () => {
    const webApp = getTelegramWebApp();
    if (webApp && webApp.HapticFeedback) {
      webApp.HapticFeedback.notificationOccurred('error');
    }
  },

  // Warning notification
  warning: () => {
    const webApp = getTelegramWebApp();
    if (webApp && webApp.HapticFeedback) {
      webApp.HapticFeedback.notificationOccurred('warning');
    }
  },

  // Selection changed - for toggles and selections
  selectionChanged: () => {
    const webApp = getTelegramWebApp();
    if (webApp && webApp.HapticFeedback) {
      webApp.HapticFeedback.selectionChanged();
    }
  }
};

// Helper function to add haptic feedback to button click handlers
export const withHapticFeedback = (callback, feedbackType = 'light') => {
  return (...args) => {
    // Trigger haptic feedback
    if (hapticFeedback[feedbackType]) {
      hapticFeedback[feedbackType]();
    }

    // Execute original callback
    if (callback) {
      return callback(...args);
    }
  };
};

// Initialize Telegram Web App with fullscreen support
export const initTelegramWebApp = () => {
  const webApp = getTelegramWebApp();
  if (webApp) {
    // Ready the web app
    webApp.ready();

    // Log Telegram WebApp version and capabilities
    console.log('🚀 Telegram WebApp initialized');
    console.log(`📱 Version: ${getTelegramVersion()}`);
    console.log(`🔥 Fullscreen available: ${isMethodSupported('requestFullscreen')}`);
    console.log(`📏 Initial viewport: ${webApp.viewportHeight}px`);
    console.log(`📐 Initial stable height: ${webApp.viewportStableHeight}px`);

    // Request fullscreen mode using safe method
    setTimeout(() => {
      const deviceType = getDeviceType();
      if (!webApp.isExpanded && deviceType === 'mobile') {
        // webApp.expand();
        viewportManager.requestFullscreen();
      }
    }, 100); // Small delay to ensure WebApp is fully ready

    // Set main button color if available
    if (webApp.MainButton) {
      webApp.MainButton.setParams({
        color: '#19619a',
        text_color: '#FFFFFF'
      });
    }

    // Listen to viewport changes (only if supported)
    if (isMethodSupported('onEvent')) {
      viewportManager.onViewportChanged(() => {
        console.log('📱 Viewport changed');
        // Update CSS variables with new dimensions
        document.documentElement.style.setProperty('--tg-viewport-height', `${webApp.viewportHeight}px`);
        document.documentElement.style.setProperty('--tg-viewport-stable-height', `${webApp.viewportStableHeight}px`);
      });

      // Listen to fullscreen changes (only if supported)
      viewportManager.onFullscreenChanged(() => {
        const isFullscreen = viewportManager.isFullscreen();
        console.log(`🔄 Fullscreen changed: ${isFullscreen ? 'ON' : 'OFF'}`);

        // Update body class based on fullscreen state
        if (isFullscreen) {
          document.body.classList.add('telegram-fullscreen');
        } else {
          document.body.classList.remove('telegram-fullscreen');
        }

        // Update CSS variables
        document.documentElement.style.setProperty('--tg-viewport-height', `${webApp.viewportHeight}px`);
        document.documentElement.style.setProperty('--tg-viewport-stable-height', `${webApp.viewportStableHeight}px`);
      });
    }

    // Set initial CSS variables
    document.documentElement.style.setProperty('--tg-viewport-height', `${webApp.viewportHeight}px`);
    document.documentElement.style.setProperty('--tg-viewport-stable-height', `${webApp.viewportStableHeight}px`);

    // Disable pull-to-refresh if available
    if (isMethodSupported('disableVerticalSwipes')) {
      webApp.disableVerticalSwipes();
    }

    return webApp;
  }
  return null;
};

// Open link using Telegram WebApp API if available, otherwise use window.open
export const openLink = (url) => {
  if (window.Telegram?.WebApp?.openLink) {
    console.log('📱 Opening link via Telegram WebApp API:', url);
    window.Telegram.WebApp.openLink(url);
  } else {
    console.log('🌐 Opening link via window.open:', url);
    window.open(url, '_blank');
  }
}; 